import { observable } from 'mobx';
import logger from '../utils/logger';
import { WeighedMaterialType } from '../constants/MaterialModels';
import { DeviceTypes } from '../constants/DeviceModels';
import { toNumber } from '../utils/numbers';
import DischargeSequence from '../containers/Discharge/DischargeSequence';
import DischargeScale from '../containers/Discharge/DischargeScale';
import { WebsocketEvent } from '../constants/WebsocketEvent';
import { DischargeAmountType } from '../containers/Discharge/DischargeAmountType';

export const ScaleTypes = {
  CEM: 'CEM',
  AGG: 'AGG',
  WATER: 'WATER',
};

class DischargeStore {
  @observable
    sequences = [];
  @observable
    truckChargeId = null;
  @observable
    truckCharge = [];
  @observable
    mixChargeId = null;
  @observable
    mixCharge = [];
  @observable
    loadSizeChargeId = null;
  @observable
    loadSizeCharge = [];
  dischargeAmountType = Object.keys(DischargeAmountType).map(v => { return { value: DischargeAmountType[v], name: v };});

  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  get api() {
    return this.rootStore.newAuthStore;
  }

  get plantId() {
    return this.rootStore.locationsStore.plantId;
  }

  get materialStore() {
    return this.rootStore.materialStore;
  }

  get devicesStore() {
    return this.rootStore.devicesStore;
  }

  get mixStore() {
    return this.rootStore.mixStore;
  }

  get defaultDischargeRate() {
    return (this.rootStore.settingsStore.configuration && this.rootStore.settingsStore.configuration.defaultDischargeRate) || 250;
  }

  getDischargeSequenceName(id) {
    const seq = this.sequences.find(v => v.id === id);
    return seq ? seq.name : id;
  }

  getSequences = () => {
    const url = `/api/plant/${this.plantId}/discharge`;
    if (!this.sequences.length) {
      this.api.storeDB.init()
        .then(() => this.api.storeDB.getByKey(url))
        .then(data => this.sequences = data ? data.map(v => new DischargeSequence(v)) : []);
    }

    return this.api
      .makeAuthorizedRequest(url)
      .then(resp => this.sequences = resp ? resp.map(v => new DischargeSequence(v)) : []);
  };

  getFinalScalesForMix(mixId) {
    const mix = this.mixStore.getMixDesign(mixId) || [];
    const scales = {
      AGG: [],
      CEM: [],
      WATER: [],
    };
    const targets = {
      AGG: 0,
      CEM: 0,
      WATER: 0,
    };

    mix.forEach(v => {
      const data = this.getDataForMaterial(v.constituentItemId);
      if (data) {
        const target = v.dosQuantity ? v.dosQuantity : v.quantity;
        const { materialDetails } = data;
        const materialType = materialDetails.type;

        if (materialType === WeighedMaterialType.CoarseAggregate || materialType === WeighedMaterialType.FineAggregate) {
          if (scales.AGG.indexOf(data.scale.id) === -1) {
            scales.AGG.push(data.scale.id);
          }
          targets.AGG += target;
        } else if (materialType === WeighedMaterialType.Water) {
          if (scales.WATER.indexOf(data.scale.id) === -1) {
            scales.WATER.push(data.scale.id);
          }
          targets.WATER += target;
        } else if (materialType === WeighedMaterialType.Cement) {
          if (scales.CEM.indexOf(data.scale.id) === -1) {
            scales.CEM.push(data.scale.id);
          }
          targets.CEM += target;
        }
      }
    });

    const finalScales = [];

    Object.keys(scales).forEach((type, i) => {
      scales[type].forEach((id, j) => {
        const isPrimary = !i && !j;
        const start = isPrimary ? 0 : type === ScaleTypes.WATER ? 1 : 20;
        const finish = isPrimary ? 100 : type === ScaleTypes.WATER ? 50 : 80;
        const s = this.devicesStore.getDeviceById(id) || {};

        const scale = new DischargeScale({
          scaleId: id,
          scaleName : s.description || '',
          type,
          target: targets[type],
          isPrimary,
          start,
          finish,
        });

        finalScales.push(scale);

      });
    });

    return finalScales;
  };

  getDataForMaterial(materialId) {
    const material = this.materialStore.getMaterialByItemId(materialId);

    if (!material) {
      logger.log(`No material: #${materialId} on the plant! `);
      return;
    }

    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    if (!materialDetails) {
      logger.log(`No material details: ${material.code}! `, material);
      return;
    }

    let bin, scale;

    if (material.scaleId) {
      scale = this.getLastScale(material.scaleId);
    } else {
      bin = this.devicesStore.getPrimaryBin(material);
      if (!bin) {
        logger.log(`No bin for ${material.code}! `, material);
        return;
      }
      scale = this.getLastScale(bin.scaleId);
    }

    return {
      material,
      materialDetails,
      bin,
      scale,
    };
  };

  isOneLevelScale(scaleId) {
    return !this.devicesStore.devices.find(v => v.parentScaleId === scaleId);
  }

  getLastScale(scaleId) {
    const scale = this.devicesStore.getDeviceById(scaleId);
    if (!scale) {
      logger.log(`No scale #${scaleId}!`);
      return;
    }

    if (scale.parentScaleId) {
      return this.getLastScale(scale.parentScaleId);
    }

    return scale;
  };

  getTypeForScale(scaleId) {
    const scale = this.devicesStore.getDeviceById(Number(scaleId));
    const materialType = this.devicesStore.getMaterialTypeForScale(scale);
    let type;

    switch(materialType) {
      case WeighedMaterialType.FineAggregate:
      case WeighedMaterialType.CoarseAggregate:
        type = ScaleTypes.AGG;
        break;
      case WeighedMaterialType.Cement:
      case WeighedMaterialType.Cementitious:
        type = ScaleTypes.CEM;
        break;
      case WeighedMaterialType.Water:
      case WeighedMaterialType.SecondWater:
        type = ScaleTypes.WATER;
        break;
      default:
        type = '';
        logger.warn(`Scale #${scaleId} has no type!`);
    }

    return type;
  };

  getUnitForScale(scaleId) {
    return this.devicesStore.getUnitForScale(scaleId);
  };

  getFinalScales() {
    const s = this.devicesStore.devices.filter(scale => scale.deviceTypeName === DeviceTypes.Scale && !scale.parentScaleId) || [];
    let scales = [];

    s.forEach(scale => {
      const type = this.getTypeForScale(scale.id);
      const unit = this.getUnitForScale(scale.id);
      scales.push({
        scaleId: scale.id,
        type,
        unit,
      });
    });

    scales.sort((a, b) => a.type === b.type ? 0 : +(a.type > b.type) || -1);

    scales.forEach((scale, i) => {
      const isPrimary = !i;
      scales[i] = new DischargeScale({
        ...scale,
        isPrimary,
        order: i + 1,
        start: isPrimary ? 0 : scale.type === ScaleTypes.WATER ? 1 : 20,
        finish: isPrimary ? 100 : scale.type === ScaleTypes.WATER ? 50 : 80,
      });
    });

    return scales;
  };

  calculateFlowRate(scales, defaultFlowRate = 100, useDiscreteDischarge = false) {
    if (useDiscreteDischarge) {
      const rate = scales.map(v => v.flowRate);
      logger.log('calculate flow rate, useDiscreteDischarge:', rate);
      return rate;
    }

    const targets = scales.map(v => v.target);
    const percent = scales.map(v => v.finish - v.start);
    const sum = targets.reduce((total, num) => total + num, 0);
    let rate = [];

    targets.forEach((target, i) => {
      rate.push(target > 0 ? toNumber(((target * defaultFlowRate) / sum) * (100 / percent[i]), 1) : 0);
    });

    logger.log('calculate flow rate:', rate);

    return rate;
  };

  addSequence(data) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/discharge`,
        data,
        'POST'
      )
      .then(resp => {
        this.sequences.push(new DischargeSequence(resp));
        return resp;
      });
  };

  updateSequence(data) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/discharge/${data.id}`,
        data,
        'PUT'
      )
      .then(resp => {
        const index = this.sequences.findIndex(v => v.id === resp.id);
        if (index > -1) {
          this.sequences[index] = new DischargeSequence(resp);
        }
        return resp;
      });
  };

  removeSequence(id) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/discharge/${id}`,
        null,
        'DELETE'
      )
      .then(() => {
        this.sequences = this.sequences.filter(v => v.id !== id);
      });
  };

  selectTruckCharge(id) {
    this.truckChargeId = id;
  };

  loadCharge = () => {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/charge`)
      .then(resp => {
        resp = resp || [];
        this.truckCharge = resp.filter(v => v.truckId);
        this.mixCharge = resp.filter(v => v.mixItemId);
        this.loadSizeCharge = resp.filter(v => v.loadSize);
        return resp;
      });

  };

  addTruckCharge(data) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/charge`,
        data,
        'POST'
      )
      .then(resp => {
        this.truckCharge.push(resp);
        return resp;
      });
  };

  updateTruckCharge(data) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/charge/${data.id}`,
        data,
        'PUT'
      )
      .then(resp => {
        const index = this.truckCharge.findIndex(v => v.id === resp.id);
        if (index > -1) {
          this.truckCharge[index] = resp;
        }
        return resp;
      });
  };

  removeTruckCharge(id) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/charge/${id}`,
        null,
        'DELETE'
      )
      .then(() => {
        if (id === this.truckChargeId) {
          this.selectMixCharge(null);
        }
        const index = this.truckCharge.findIndex(v => v.id === id);
        if (index > -1) {
          this.truckCharge.splice(index, 1);
        }
        return id;
      });
  };

  selectMixCharge(id) {
    this.mixChargeId = id;
  };

  selectLoadSizeCharge(id) {
    this.loadSizeChargeId = id;
  };

  addMixCharge(data) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/charge`,
        data,
        'POST'
      )
      .then(resp => {
        this.mixCharge.push(resp);
        return resp;
      });
  };

  addLoadSizeCharge(data) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/charge`,
        data,
        'POST'
      )
      .then(resp => {
        this.loadSizeCharge.push(resp);
        return resp;
      });
  };

  updateMixCharge(data) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/charge/${data.id}`,
        data,
        'PUT'
      )
      .then(resp => {
        const index = this.mixCharge.findIndex(v => v.id === resp.id);
        if (index > -1) {
          this.mixCharge[index] = resp;
        }
        return resp;
      });
  };

  updateLoadSizeCharge(data) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/charge/${data.id}`,
        data,
        'PUT'
      )
      .then(resp => {
        const index = this.loadSizeCharge.findIndex(v => v.id === resp.id);
        if (index > -1) {
          this.loadSizeCharge[index] = resp;
        }
        return resp;
      });
  };

  removeMixCharge(id) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/charge/${id}`,
        null,
        'DELETE'
      )
      .then(() => {
        if (id === this.mixChargeId) {
          this.selectMixCharge(null);
        }
        const index = this.mixCharge.findIndex(v => v.id === id);
        if (index > -1) {
          this.mixCharge.splice(index, 1);
        }
        return id;
      });
  };

  removeLoadSizeCharge(id) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/charge/${id}`,
        null,
        'DELETE'
      )
      .then(() => {
        if (id === this.loadSizeChargeId) {
          this.selectLoadSizeCharge(null);
        }
        const index = this.loadSizeCharge.findIndex(v => v.id === id);
        if (index > -1) {
          this.loadSizeCharge.splice(index, 1);
        }
        return id;
      });
  };

  getDefaultSequence() {
    const scales = this.getFinalScales();
    const defaultFlowRate = this.defaultDischargeRate;
    const speeds = this.calculateFlowRate(scales, defaultFlowRate);

    scales.forEach((scale, i) => {
      scales[i].flowRate = speeds[i];
    });

    return new DischargeSequence({
      allWaterOnFirstDrop: false,
      defaultFlowRate,
      discharges: scales,
    });
  };

  startWebSockets = () => {
    const { notifierStore, websocketStore, connectionStore, settingsStore } = this.rootStore;
    this.ws = websocketStore.connection;
    clearTimeout(this.wsTimer);

    if (!this.ws || !connectionStore.isOnline || !websocketStore.isTrulyConnected) {
      this.wsTimer = setTimeout(() => {
        this.startWebSockets();
      }, 2000);
      return;
    }

    this.ws.on(WebsocketEvent.DISCHARGE_ADD, resp => {
      logger.log('ws:', WebsocketEvent.DISCHARGE_ADD, resp);

      if (resp) {
        this.getSequences();
        if (settingsStore.notificationSettings.discharge) {
          notifierStore.showInfo(`New discharge sequence ${resp.id} added!`);
        }
      }
    });

    this.ws.on(WebsocketEvent.DISCHARGE_UPDATE, resp => {
      logger.log('ws:', WebsocketEvent.DISCHARGE_UPDATE, resp);

      if (resp) {
        this.getSequences();
        if (settingsStore.notificationSettings.discharge) {
          notifierStore.showInfo(`Discharge sequence ${resp.id} updated!`);
        }
      }
    });

    this.ws.on(WebsocketEvent.DISCHARGE_DELETE, resp => {
      logger.log('ws:', WebsocketEvent.DISCHARGE_DELETE, resp);

      if (resp) {
        this.sequences = this.sequences.filter(v => v.id !== resp.id);
        if (settingsStore.notificationSettings.discharge) {
          notifierStore.showInfo(`Discharge sequence ${resp.id || ''} deleted!`);
        }
      }
    });

    this.ws.on(WebsocketEvent.CHARGE_ADD, resp => {
      logger.log('ws:', WebsocketEvent.CHARGE_ADD, resp);

      if (resp) {
        this.loadCharge()
          .then(() => {
            const mixCharge = this.mixCharge.find(v => v.id === resp.id);
            if (settingsStore.notificationSettings.mixCharge && mixCharge) {
              notifierStore.showInfo(`Mix charge ${resp.id || ''} added!`);
            } else if (settingsStore.notificationSettings.truckCharge) {
              notifierStore.showInfo(`Truck charge ${resp.id || ''} added!`);
            }
          });

      }
    });

    this.ws.on(WebsocketEvent.CHARGE_UPDATE, resp => {
      logger.log('ws:', WebsocketEvent.CHARGE_UPDATE, resp);

      if (resp) {
        const mixCharge = this.mixCharge.find(v => v.id === resp.id);
        this.loadCharge();
        if (settingsStore.notificationSettings.mixCharge && mixCharge) {
          notifierStore.showInfo(`Mix charge ${resp.id || ''} updated!`);
        } else if (settingsStore.notificationSettings.truckCharge) {
          notifierStore.showInfo(`Truck charge ${resp.id || ''} updated!`);
        }
      }
    });

    this.ws.on(WebsocketEvent.CHARGE_DELETE, resp => {
      logger.log('ws:', WebsocketEvent.CHARGE_DELETE, resp);

      if (resp) {
        const mixCharge = this.mixCharge.find(v => v.id === resp.id);
        this.loadCharge();
        if (settingsStore.notificationSettings.mixCharge && mixCharge) {
          notifierStore.showInfo(`Mix charge ${resp.id || ''} deleted!`);
        } else if (settingsStore.notificationSettings.truckCharge) {
          notifierStore.showInfo(`Truck charge ${resp.id || ''} deleted!`);
        }
      }
    });

    this.ws.onclose(this.startWebSockets);
  };
}

export default DischargeStore;
